import { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import MobileCard from './MobileCard'
import { colors } from '../../const/colors'
import DeleteHIDCard from './DeleteHIDCard'
import { useTranslation } from 'react-i18next'
import {
  IOrigoCard,
  OrigoEventName,
  GET_ORIGO_ACCOUNT,
  IGetOrigoAccountData,
  IGetOrigoAccountVars,
} from '../../graphql/hid/getOrigoAccount'
import { useLazyQuery, ApolloError } from '@apollo/client'
import { IUserDataRow } from './HIDUsersTable'
import Error from '../Shared/Error'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'

const statusesDuringRemoval = [
  OrigoEventName.CREDENTIAL_REVOKED,
  OrigoEventName.CREDENTIAL_REVOKING,
  OrigoEventName.ENDPOINT_TERMINATED,
]

const HIDCards = ({
  userData,
  refetch,
}: {
  userData: IUserDataRow
  refetch: () => void
}) => {
  const { t } = useTranslation(['hid'])
  const [errorMessage, setErrorMessage] = useState('')
  const [deletionProcess, setDeletionProcess] = useState(false)
  const [calls, setCalls] = useState(0)
  const [loading, setLoading] = useState(false)
  const [cards, setCards] = useState<IOrigoCard[]>([])
  const { login } = userData

  const [getMobileAccessAccount] = useLazyQuery<
    IGetOrigoAccountData,
    IGetOrigoAccountVars
  >(GET_ORIGO_ACCOUNT, {
    variables: { login },
    fetchPolicy: 'no-cache',
    onError: (error: ApolloError) => {
      setLoading(false)
      setErrorMessage(error.message)
    },
    onCompleted: (data: IGetOrigoAccountData) => {
      setCards(data?.getUser.origoAccounts[0]?.origoCards || [])
      if (!data?.getUser.origoAccounts[0]?.origoCards.length && calls <= 7) {
        setCalls(calls + 1)
        setTimeout(getMobileAccessAccount, 1500)
      } else {
        setLoading(false)
      }
    },
  })

  const refetchData = () => {
    refetch()
    getMobileAccessAccount()
  }

  useEffect(() => {
    getMobileAccessAccount()
    if (!cards.length) {
      setLoading(true)
    }
  }, [])

  if (calls > 7 && !cards.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          mt: '4px',
          p: '14px 16px',
          borderRadius: '12px',
          background: colors.redBg,
          border: `1px solid ${colors.redBorder}`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '9px',
            background: colors.red,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 18, color: '#fff' }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: colors.red, lineHeight: 1.4 }}>
            {t('no_card_created')}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: colors.red, opacity: 0.8, lineHeight: 1.55, mt: '3px' }}>
            {t('no_card_created_desc')}
          </Typography>
        </Box>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          mt: '4px',
          p: '14px 16px',
          borderRadius: '12px',
          background: '#FFF8E6',
          border: '1.5px solid #F9D06B',
          animation: 'pulseCard 2s ease-in-out infinite',
          '@keyframes pulseCard': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.6 },
          },
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '9px',
            background: '#F1901D',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            animation: 'flipHourglass 2s ease-in-out infinite',
            '@keyframes flipHourglass': {
              '0%, 40%': { transform: 'rotate(0deg)' },
              '60%, 100%': { transform: 'rotate(180deg)' },
            },
          }}
        >
          <HourglassEmptyOutlinedIcon sx={{ fontSize: 17, color: '#fff' }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#9A5A00', lineHeight: 1.4 }}>
            {t('card_under_creation')}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: '#B06E10', lineHeight: 1.55, mt: '3px' }}>
            {t('card_under_creation_desc')}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {cards.map((item, i) => (
        <Box
          key={i}
          sx={{
            borderRadius: '16px',
            p: '18px',
            background: item.current ? colors.bg : '#ECEEF1',
            boxShadow: item.current
              ? '0 8px 32px -12px rgba(19,54,73,.28)'
              : 'none',
            border: item.current
              ? `1.5px solid ${colors.border3}`
              : `1px solid ${colors.border}`,
          }}
        >
          <MobileCard card={item} />
          {item.current &&
            !!item.deviceId &&
            !statusesDuringRemoval.includes(item.status) &&
            !deletionProcess && (
              <Box
                sx={{
                  mt: '16px',
                  pt: '16px',
                  borderTop: `1px solid ${colors.border}`,
                }}
              >
                <DeleteHIDCard
                  refetch={refetchData}
                  card={item}
                  setDeletionProcess={setDeletionProcess}
                />
              </Box>
            )}
          {(statusesDuringRemoval.includes(item.status) || deletionProcess) && (
            <Box
              data-e2e="card-deletion-in-progress"
              sx={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                mt: '14px',
                p: '12px 14px',
                borderRadius: '10px',
                background: colors.redBg,
                border: `1px solid ${colors.redBorder}`,
                animation: 'pulseBanner 1.6s ease-in-out infinite',
                '@keyframes pulseBanner': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.65 },
                },
              }}
            >
              <HourglassEmptyOutlinedIcon
                sx={{
                  fontSize: 17,
                  color: colors.red,
                  flexShrink: 0,
                  animation: 'flipHourglass 1.6s ease-in-out infinite',
                  '@keyframes flipHourglass': {
                    '0%, 40%': { transform: 'rotate(0deg)' },
                    '60%, 100%': { transform: 'rotate(180deg)' },
                  },
                }}
              />
              <Typography sx={{ fontSize: '13px', color: colors.red, lineHeight: 1.5 }}>
                {t('card_deletion_in_progress')}
              </Typography>
            </Box>
          )}
        </Box>
      ))}
      {errorMessage && <Error errorMessage={errorMessage} />}
    </Box>
  )
}

export default HIDCards
